// src/pages/TournamentPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { subscribeToAuth } from '../auth';
import AppShell from '../components/AppShell';
import { ArrowLeftIcon, MoreIcon, RankingIcon, RoundsIcon, ShareIcon, UsersIcon } from '../components/icons';
import ShareDialog from '../components/ShareDialog';
import HistoryPanel from './tournament/HistoryPanel';
import FirstSteps from '../components/FirstSteps';
import CoOrganizersDialog from '../collab/CoOrganizersDialog';
import { useCollaboration } from '../collab/useCollaboration';
import { notifyDataChanged } from '../testMode/testMode';
import { localEngineEnabled } from '../engine/engineFlag';
import { useSync } from '../sync/SyncProvider';
import PlayerDetailDialog from '../components/PlayerDetailDialog';
import EditableName from '../components/EditableName';
import { useTournament } from './tournament/useTournament';
import PlayersPanel from './tournament/PlayersPanel';
import RoundsPanel from './tournament/RoundsPanel';
import RankingPanel from './tournament/RankingPanel';
import TournamentDetails from './tournament/TournamentDetails';
import AlgorithmSettings from './tournament/AlgorithmSettings';
import SinglesSetting from './tournament/SinglesSetting';
import FormatSetting from './tournament/FormatSetting';
import GroupsSetting from './tournament/GroupsSetting';
import TeamsPanel from './tournament/TeamsPanel';
import TournamentActions from './tournament/TournamentActions';
import { useRanking } from './tournament/useRanking';

// A short, filesystem-safe name for the exported file, e.g. "Friday Cup!" -> "friday-cup".
const slugify = (name) =>
    name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'tournament';

function downloadJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

// The parts of a tournament, each on its own view.
const VIEWS = [
    ['rounds', 'Rounds'],
    ['ranking', 'Ranking'],
    ['players', 'Players'],
    ['settings', 'Settings'],
    ['history', 'History'],
];

function TournamentPage() {
    const user = useContext(UserContext);
    const { tournamentId } = useParams();
    const navigate = useNavigate();
    const tournament = useTournament(tournamentId, user);
    // Which part of the tournament is shown: kept in the address (?view=players), so a link or the back button lands there.
    const [searchParams, setSearchParams] = useSearchParams();
    const asked = searchParams.get('view');
    const [moreOpen, setMoreOpen] = useState(false);
    const [printing, setPrinting] = useState(null); // the view to print, once it is on screen
    const [sharing, setSharing] = useState(false);
    const [coorganizing, setCoorganizing] = useState(false);
    const collab = useCollaboration(user)(tournament.details);
    const sync = useSync();
    const [duplicating, setDuplicating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [detailPlayer, setDetailPlayer] = useState(null);
    const activePlayerCount = tournament.players.filter((player) => player.active !== false).length;
    // A tournament with nobody in it starts on the players; otherwise on the rounds.
    // Decided once, when the tournament has loaded: adding the first player must not move you off the players.
    const [startView, setStartView] = useState(null);
    useEffect(() => {
        if (!tournament.loading && startView === null) setStartView(tournament.players.length === 0 ? 'players' : 'rounds');
    }, [tournament.loading, tournament.players.length, startView]);
    const view = VIEWS.some(([key]) => key === asked) ? asked : startView || 'rounds';
    const setView = (next) => {
        setMoreOpen(false);
        setSearchParams({ view: next }, { replace: true });
    };
    const grouped = tournament.details.format === 'groups-finals';
    const byTeam = tournament.details.format === 'fixed-teams' || grouped;
    // Waits for the tournament's own load (format included), so it never fetches the wrong endpoint first.
    const ranking = useRanking(tournamentId, user, {
        version: tournament.version, enabled: view === 'ranking' && !tournament.loading, byTeam,
    });
    const locked = Boolean(tournament.details.finished);

    // Arrow keys, Home and End move between the tabs (the tab row is one stop for the Tab key).
    const moveBetweenTabs = (event, key) => {
        const at = VIEWS.findIndex(([candidate]) => candidate === key);
        const target = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: VIEWS.length - 1 }[event.key];
        if (target === undefined) return;
        event.preventDefault();
        const next = VIEWS[(target + VIEWS.length) % VIEWS.length][0];
        setView(next);
        document.getElementById(`view-tab-${next}`)?.focus();
    };

    // Printing shows the rounds or the ranking (not the settings page the button is on), and waits for them to be there.
    useEffect(() => {
        if (!printing) return undefined;
        if (view !== printing) {
            setView(printing);
            return undefined;
        }
        if (printing === 'ranking' && ranking.loading) return undefined;
        const timer = setTimeout(() => {
            setPrinting(null);
            window.print();
        }, 150);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printing, view, ranking.loading]);

    // Signed out (or the session ended): back to the login page instead of an endless loading state.
    useEffect(() => {
        const unsubscribe = subscribeToAuth((authUser) => {
            if (!authUser) navigate('/');
        });
        return () => unsubscribe?.();
    }, [navigate]);

    // Others may change a shared tournament: look again now and then while the page is in view.
    const shared = collab.shared;
    const syncNow = sync.syncNow;
    useEffect(() => {
        if (!shared) return undefined;
        const look = () => {
            if (document.visibilityState !== 'visible') return;
            if (localEngineEnabled()) syncNow?.(); // brings their changes onto this device (and refreshes what is shown)
            else notifyDataChanged();
        };
        const timer = setInterval(look, 20000);
        document.addEventListener('visibilitychange', look);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', look);
        };
    }, [shared, syncNow]);

    const handleDuplicate = async () => {
        setDuplicating(true);
        const copy = await tournament.duplicateTournament();
        setDuplicating(false);
        if (copy) navigate(`/tournament/${copy.tournamentId}`);
    };

    const handleExport = async () => {
        setExporting(true);
        const data = await tournament.exportData();
        setExporting(false);
        if (data) downloadJson(data, `${slugify(tournament.name)}-export.json`);
    };

    return (
        <AppShell user={user}>
            <div className="page-head">
                <div className="titles">
                    <button className="btn btn-ghost btn-sm back-link" onClick={() => navigate('/dashboard')}>
                        <ArrowLeftIcon size={16} /> Back to dashboard
                    </button>
                    <EditableName
                        as="h1"
                        name={tournament.name}
                        inputLabel="Tournament name"
                        onSave={async (name) => Boolean(await tournament.renameTournament(name))}
                    />
                </div>
                <div className="page-actions">
                    {collab.role === 'owner' && !user?.isGuest && (
                        <button className="btn" onClick={() => setSharing(true)} disabled={tournament.loading}>
                            <ShareIcon size={16} /> {tournament.details.shareToken ? 'Sharing' : 'Share'}
                        </button>
                    )}
                    {!user?.isGuest && (
                        <button className="btn" onClick={() => setCoorganizing(true)} disabled={tournament.loading}>
                            <UsersIcon size={16} /> {collab.shared && (collab.role !== 'owner' || collab.coOrganizerCount > 0) ? 'Co-organizers' : 'Invite'}
                        </button>
                    )}
                </div>
            </div>

            {detailPlayer && (
                <PlayerDetailDialog
                    player={detailPlayer}
                    fetchGames={tournament.getPlayerGames}
                    onClose={() => setDetailPlayer(null)}
                />
            )}

            {coorganizing && (
                <CoOrganizersDialog
                    name={tournament.name}
                    tournamentId={tournamentId}
                    user={user}
                    onClose={() => setCoorganizing(false)}
                    onLeft={() => {
                        if (localEngineEnabled()) sync.syncNow?.();
                        navigate('/dashboard');
                    }}
                />
            )}

            {sharing && (
                <ShareDialog
                    name={tournament.name}
                    token={tournament.details.shareToken}
                    onStart={tournament.startSharing}
                    onStop={tournament.stopSharing}
                    onClose={() => setSharing(false)}
                />
            )}

            <div className="view-tabs tournament-tabs" role="tablist" aria-label="Parts of the tournament">
                {VIEWS.map(([key, label]) => (
                    <button
                        key={key}
                        role="tab"
                        id={`view-tab-${key}`}
                        aria-selected={view === key}
                        aria-controls="view-panel"
                        tabIndex={view === key ? 0 : -1}
                        className={`view-tab${view === key ? ' active' : ''}`}
                        onClick={() => setView(key)}
                        onKeyDown={(event) => moveBetweenTabs(event, key)}
                    >
                        {label}
                        {key === 'players' && !tournament.loading && <span className="tab-count">{activePlayerCount}</span>}
                    </button>
                ))}
            </div>

            {/* Phones: a bar at the bottom, with the rarely used parts under More. Wider screens use the tabs above. */}
            <nav className="bottom-nav" aria-label="Tournament views">
                {moreOpen && (
                    <div className="bottom-more" role="menu">
                        {[['settings', 'Settings'], ['history', 'History']].map(([key, label]) => (
                            <button key={key} role="menuitem" className={view === key ? 'active' : ''} onClick={() => setView(key)}>
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                {[
                    ['rounds', 'Rounds', RoundsIcon],
                    ['ranking', 'Ranking', RankingIcon],
                    ['players', 'Players', UsersIcon],
                ].map(([key, label, Glyph]) => (
                    <button key={key} className={view === key ? 'active' : ''} aria-current={view === key ? 'page' : undefined} onClick={() => setView(key)}>
                        <Glyph size={20} />
                        <span>{label}</span>
                    </button>
                ))}
                <button
                    className={view === 'settings' || view === 'history' ? 'active' : ''}
                    aria-expanded={moreOpen}
                    onClick={() => setMoreOpen((open) => !open)}
                >
                    <MoreIcon size={20} />
                    <span>More</span>
                </button>
            </nav>

            <div id="view-panel" role="tabpanel" aria-labelledby={`view-tab-${view}`}>
            {view === 'rounds' && (
                <RoundsPanel
                    rounds={tournament.rounds}
                    activePlayerCount={activePlayerCount}
                    players={tournament.players}
                    fixedTeams={tournament.fixedTeams}
                    byTeam={byTeam}
                    grouped={grouped}
                    details={tournament.details}
                    loading={tournament.loading}
                    actions={tournament}
                    locked={locked}
                    onGoToPlayers={() => setView('players')}
                />
            )}

            {view === 'ranking' && (
                <RankingPanel
                    standings={ranking.standings}
                    ranking={ranking.ranking}
                    loading={ranking.loading}
                    failed={ranking.failed}
                    byTeam={byTeam}
                    grouped={grouped}
                    onOpenFinal={() => navigate(`/tournament/${tournamentId}/final`)}
                />
            )}

            {view === 'players' && (
                <>
                    {/* stays while players are being added: it disappearing would shift the page under your finger */}
                    {startView === 'players' && tournament.rounds.length === 0 && <FirstSteps />}
                    <PlayersPanel
                        players={tournament.players}
                        loading={tournament.loading}
                        onAdd={tournament.addPlayer}
                        onSetActive={tournament.setPlayerActive}
                        onSetLevel={tournament.setPlayerLevel}
                        onRename={tournament.renamePlayer}
                        onDelete={tournament.deletePlayer}
                        onUpload={tournament.uploadPlayers}
                        searchPrevious={tournament.searchPreviousPlayers}
                        onShowDetail={setDetailPlayer}
                        locked={locked}
                    />
                    {byTeam && !tournament.loading && (
                        <TeamsPanel
                            players={tournament.players}
                            fixedTeams={tournament.fixedTeams}
                            onAdd={tournament.addFixedTeam}
                            onDelete={tournament.deleteFixedTeam}
                            grouped={grouped}
                            groupCount={tournament.details.groupCount}
                            onMove={tournament.moveFixedTeam}
                            onRegroup={tournament.regroupTeams}
                            locked={locked || tournament.rounds.length > 0}
                        />
                    )}
                </>
            )}

            {view === 'settings' && !tournament.loading && (
                <>
                    <TournamentDetails
                        details={tournament.details}
                        onSave={async (changes) => Boolean(await tournament.updateDetails(changes))}
                        onSetFinished={(finished) => tournament.updateDetails({ finished })}
                    />
                    <FormatSetting
                        format={tournament.details.format || 'rotating'}
                        onSetFormat={(format) => tournament.updateDetails({ format })}
                        locked={tournament.rounds.length > 0 || tournament.fixedTeams.length > 0}
                    />
                    {grouped && (
                        <GroupsSetting
                            details={tournament.details}
                            onChange={(changes) => tournament.updateDetails(changes)}
                            groupsLocked={tournament.rounds.length > 0}
                            knockoutStarted={tournament.rounds.some((round) => round.stage === 'knockout')}
                            locked={locked}
                        />
                    )}
                    {!byTeam && (
                        <SinglesSetting
                            details={tournament.details}
                            activePlayerCount={activePlayerCount}
                            onSetSingles={(enabled) => tournament.updateDetails({ singlesEnabled: enabled })}
                            locked={locked}
                        />
                    )}
                    <AlgorithmSettings
                        details={tournament.details}
                        loadCatalog={tournament.getAlgorithms}
                        onSave={async (changes) => Boolean(await tournament.updateAlgorithms(changes))}
                    />
                    <TournamentActions
                        onDuplicate={handleDuplicate}
                        onExport={handleExport}
                        onPrint={setPrinting}
                        duplicating={duplicating}
                        exporting={exporting}
                    />
                </>
            )}

            {view === 'history' && !tournament.loading && (
                <HistoryPanel tournamentId={tournamentId} userId={user?.uid} standalone />
            )}
            </div>
        </AppShell>
    );
}

export default TournamentPage;
